import { Injectable, effect, inject, signal } from '@angular/core';
import { CanonicalReviewCase } from '../cases/case.models';
import { REVIEW_CASE_FIXTURES } from './review-fixtures';
import { ReviewScenarioService } from './review-scenario.service';
import { ReviewSessionService } from './review-session.service';

const ACTOR = 'reviewer-a';

@Injectable({ providedIn: 'root' })
export class ReviewCaseRepository {
  private readonly session = inject(ReviewSessionService);
  private readonly scenarios = inject(ReviewScenarioService);
  private readonly records = signal<CanonicalReviewCase[]>(this.restore());
  private activeScenarioId = this.scenarios.selectedId();
  private resetSeen = this.scenarios.resetSequence();

  constructor() {
    effect(() => {
      const scenarioId = this.scenarios.selectedId();
      const resetSequence = this.scenarios.resetSequence();
      if (scenarioId !== this.activeScenarioId) {
        this.activeScenarioId = scenarioId;
        this.records.set(this.restore());
      }
      if (resetSequence !== this.resetSeen) {
        this.resetSeen = resetSequence;
        this.reset();
      }
    });
  }

  list(): readonly CanonicalReviewCase[] {
    return this.scenarios.selectedId() === 'empty' ? [] : this.clone(this.records());
  }
  get(caseNumber: string): CanonicalReviewCase | null {
    if (this.scenarios.selectedId() === 'empty') return null;
    const item = this.records().find((record) => record.forthrightCaseNumber === caseNumber.toUpperCase());
    return item ? this.clone(item) : null;
  }

  update(caseNumber: string, expectedRevision: number, update: (record: CanonicalReviewCase) => void): CanonicalReviewCase | null {
    const records = this.clone(this.records());
    const item = records.find((record) => record.forthrightCaseNumber === caseNumber.toUpperCase());
    if (!item || item.revision !== expectedRevision) return null;
    update(item);
    item.revision += 1;
    this.records.set(records);
    this.persist(records);
    return this.clone(item);
  }

  syncPartySubmission(caseNumber: string, lines: readonly { readonly id: string; readonly amountCents: number | null }[], narrative: string, attributes: Readonly<Record<string, string>>): void {
    const records = this.clone(this.records());
    const item = records.find((record) => record.forthrightCaseNumber === caseNumber.toUpperCase());
    if (!item) return;
    for (const line of item.lineItems) {
      const incoming = lines.find((candidate) => candidate.id === line.id);
      const offer = line.offers.find((candidate) => candidate.organizationId === item.partyOrganizationId);
      if (incoming && offer) {
        offer.amountCents = incoming.amountCents;
        offer.narrative = narrative;
        offer.attributes = { ...attributes };
        offer.submitted = true;
      }
    }
    item.revision += 1;
    this.records.set(records);
    this.persist(records);
  }

  reset(): void {
    this.session.remove(ACTOR, this.storageName());
    this.records.set(this.clone([...REVIEW_CASE_FIXTURES]));
  }
  private storageName(): string {
    return `cases.${this.scenarios.selectedId()}`;
  }
  private restore(): CanonicalReviewCase[] {
    return this.session.read<CanonicalReviewCase[]>(ACTOR, this.storageName()) ?? this.clone([...REVIEW_CASE_FIXTURES]);
  }
  private persist(records: CanonicalReviewCase[]): void {
    this.session.write(ACTOR, this.storageName(), records);
  }
  private clone<T>(value: T): T {
    return JSON.parse(JSON.stringify(value)) as T;
  }
}
